import { useState, useEffect, useCallback } from "react";
import { invokeFunction } from "@/lib/supabase-functions";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@pps/ui/hooks/use-toast";

let cachedVapidKey: string | null = null;
let vapidKeyPromise: Promise<string | null> | null = null;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function fetchVapidKeyFromServer(): Promise<string | null> {
  if (cachedVapidKey) return cachedVapidKey;
  if (vapidKeyPromise) return vapidKeyPromise;
  vapidKeyPromise = (async () => {
    try {
      const { data, error } = await invokeFunction("get-vapid-key");
      if (error) { console.error("Error fetching VAPID key:", error instanceof Error ? error.message : String(error)); return null; }
      if (data?.vapidPublicKey) { cachedVapidKey = data.vapidPublicKey; return cachedVapidKey; }
      return null;
    } catch (error) { console.error("Error fetching VAPID key:", error instanceof Error ? error.message : String(error)); return null; }
    finally { vapidKeyPromise = null; }
  })();
  return vapidKeyPromise;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
    setIsSupported(supported);
    if (!supported) { setIsInitializing(false); return; }
    setPermission(Notification.permission);

    const init = async () => {
      try {
        const tasks: Promise<void>[] = [fetchVapidKeyFromServer().then(() => {})];
        if (user) {
          tasks.push(
            navigator.serviceWorker.ready
              .then((reg) => reg.pushManager.getSubscription())
              .then((sub: PushSubscription | null) => setIsSubscribed(!!sub))
              .catch((err: unknown) => console.error("Error checking subscription:", err instanceof Error ? err.message : String(err)))
          );
        }
        await Promise.all(tasks);
      } finally { setIsInitializing(false); }
    };
    init();
  }, [user]);

  const subscribe = useCallback(async () => {
    if (!user || !isSupported) return false;
    setIsLoading(true);
    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      if (permissionResult !== "granted") {
        toast({ title: "Permissão negada", description: "Você precisa permitir notificações para receber lembretes.", variant: "destructive" });
        return false;
      }
      let vapidKey = cachedVapidKey;
      if (!vapidKey) vapidKey = await fetchVapidKeyFromServer();
      if (!vapidKey) {
        toast({ title: "Erro de configuração", description: "Notificações push não estão configuradas corretamente.", variant: "destructive" });
        return false;
      }
      const registration = await navigator.serviceWorker.ready;
      const applicationServerKey = urlBase64ToUint8Array(vapidKey);
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey.buffer as ArrayBuffer });
      const p256dh = subscription.getKey("p256dh");
      const auth = subscription.getKey("auth");
      if (!p256dh || !auth) throw new Error("Failed to get subscription keys");
      const p256dhBase64 = btoa(String.fromCharCode(...new Uint8Array(p256dh)));
      const authBase64 = btoa(String.fromCharCode(...new Uint8Array(auth)));
      const { error } = await invokeFunction("register-push", { body: { endpoint: subscription.endpoint, p256dh: p256dhBase64, auth: authBase64 } });
      if (error) throw error;
      setIsSubscribed(true);
      toast({ title: "Notificações ativadas! 🔔", description: "Você receberá lembretes sobre suas reservas e partidas." });
      return true;
    } catch (error) {
      console.error("Error subscribing to push:", error instanceof Error ? error.message : String(error));
      toast({ title: "Erro ao ativar notificações", description: "Não foi possível configurar as notificações push.", variant: "destructive" });
      return false;
    } finally { setIsLoading(false); }
  }, [user, isSupported, toast]);

  const unsubscribe = useCallback(async () => {
    if (!user) return false;
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        const { error } = await invokeFunction("register-push", { body: { endpoint: subscription.endpoint, action: "unsubscribe" } });
        if (error) throw error;
      }
      setIsSubscribed(false);
      toast({ title: "Notificações desativadas", description: "Você não receberá mais lembretes." });
      return true;
    } catch (error) {
      console.error("Error unsubscribing from push:", error instanceof Error ? error.message : String(error));
      toast({ title: "Erro ao desativar", description: "Não foi possível desativar as notificações.", variant: "destructive" });
      return false;
    } finally { setIsLoading(false); }
  }, [user, toast]);

  return { permission, isSubscribed, isLoading, isSupported, isInitializing, subscribe, unsubscribe };
}